import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, fmtError } from "@/lib/api";
import { Heart, Car, ShieldAlert, Flame, Baby, PersonStanding, AlertTriangle, ShieldPlus, Stethoscope, Building2, Users, HelpCircle, X, Loader2, MapPin, Check, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { LiveMap } from "@/components/LiveMap";

const TYPES = [
  { id: "medical", label: "Medical Emergency", icon: Heart, color: "#DC2626" },
  { id: "road_accident", label: "Road Accident", icon: Car, color: "#F97316" },
  { id: "personal_safety", label: "Personal Safety", icon: ShieldAlert, color: "#7c3aed" },
  { id: "fire", label: "Fire", icon: Flame, color: "#ea580c" },
  { id: "child", label: "Child Emergency", icon: Baby, color: "#0891b2" },
  { id: "elderly", label: "Elderly Emergency", icon: PersonStanding, color: "#0d9488" },
  { id: "other", label: "Other", icon: AlertTriangle, color: "#6b7280" },
];
const NEEDS = [
  { id: "Medical Help", icon: Stethoscope }, { id: "Police", icon: ShieldPlus },
  { id: "Fire", icon: Flame }, { id: "Neighbour Assistance", icon: Users }, { id: "Unsure", icon: HelpCircle },
];

export default function SosFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [type, setType] = useState(null);
  const [able, setAble] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [coords, setCoords] = useState(null);
  const [locStatus, setLocStatus] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [testMode, setTestMode] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setTestMode(!!r.data?.emergency_test_mode)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setStep(1), 1900); return () => clearTimeout(t); }, []);

  const captureGps = () => {
    setLocStatus("locating");
    if (!navigator.geolocation) { setLocStatus("fallback"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); setLocStatus("ok"); },
      () => { setLocStatus("fallback"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const create = async (overrideAble) => {
    setBusy(true);
    try {
      const payload = { type, able_to_respond: overrideAble ?? able, needs, ...(coords || {}) };
      const { data } = await api.post("/emergencies", payload);
      if (data.warning) toast.warning(data.warning, { duration: 6000 });
      navigate(`/emergency/${data.id}`, { replace: true });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); setBusy(false); }
  };

  const exit = () => navigate("/home");

  const shell = (children) => (
    <div className="min-h-screen bg-[#111827] text-white flex flex-col">
      <div className="max-w-md w-full mx-auto px-6 py-6 flex-1 flex flex-col">
        {step > 0 && <button onClick={exit} data-testid="sos-cancel" className="self-end w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mb-2"><X className="w-5 h-5" /></button>}
        {testMode && (
          <div data-testid="test-mode-banner" className="flex items-start gap-2.5 bg-[#F59E0B]/15 border border-[#F59E0B]/40 rounded-2xl px-4 py-3 mb-3">
            <FlaskConical className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />
            <p className="text-[13px] leading-snug text-[#FCD34D] font-semibold"><span className="uppercase tracking-wide">Test mode</span> — this is a practice run. No responders or emergency services are contacted.</p>
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }} className="flex-1 flex flex-col">
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );

  if (step === 0)
    return shell(
      <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="sos-preparing">
        <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }} transition={{ duration: 1.4, repeat: Infinity }} className="w-24 h-24 rounded-full bg-[#DC2626] flex items-center justify-center mb-8 shadow-[0_0_60px_rgba(220,38,38,0.6)]">
          <ShieldPlus className="w-12 h-12 text-white" />
        </motion.div>
        <h1 className="text-2xl font-bold font-head">Guardian AI is preparing your emergency</h1>
        <p className="text-white/60 mt-2">Setting up secure coordination…</p>
      </div>
    );

  if (step === 1)
    return shell(
      <div>
        <h1 className="text-3xl font-bold font-head">What happened?</h1>
        <p className="text-white/60 mt-1 mb-6">Choose the type of emergency.</p>
        <div className="grid grid-cols-1 gap-3">
          {TYPES.map((t) => (
            <button key={t.id} data-testid={`sos-type-${t.id}`} onClick={() => { setType(t.id); setStep(2); }} className="flex items-center gap-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-4 text-left transition-colors active:scale-[0.98] min-h-[64px]">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${t.color}22` }}><t.icon className="w-6 h-6" style={{ color: t.color }} /></div>
              <span className="text-lg font-semibold">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    );

  if (step === 2)
    return shell(
      <div className="flex-1 flex flex-col">
        <h1 className="text-3xl font-bold font-head">Are you able to respond?</h1>
        <p className="text-white/60 mt-1 mb-8">This helps Guardian AI understand urgency.</p>
        <div className="grid grid-cols-2 gap-4">
          <button data-testid="sos-able-yes" onClick={() => { setAble(true); setStep(3); }} className="bg-[#16A34A] rounded-3xl py-10 text-2xl font-black flex flex-col items-center gap-2 active:scale-95 transition-transform"><Check className="w-10 h-10" /> YES</button>
          <button data-testid="sos-able-no" onClick={() => { setAble(false); captureGps(); create(false); }} className="bg-[#DC2626] rounded-3xl py-10 text-2xl font-black flex flex-col items-center gap-2 active:scale-95 transition-transform"><AlertTriangle className="w-10 h-10" /> NO</button>
        </div>
        {busy && <p className="text-center text-white/70 mt-6 flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Creating emergency…</p>}
        <p className="text-white/40 text-sm mt-auto pt-8">If you select NO, Guardian AI immediately creates the emergency and skips remaining questions.</p>
      </div>
    );

  if (step === 3)
    return shell(
      <div>
        <h1 className="text-3xl font-bold font-head">Do you need…</h1>
        <p className="text-white/60 mt-1 mb-6">Select all that apply.</p>
        <div className="space-y-3">
          {NEEDS.map((n) => {
            const on = needs.includes(n.id);
            return (
              <button key={n.id} data-testid={`sos-need-${n.id.replace(/\s/g, "-")}`} onClick={() => setNeeds(on ? needs.filter((x) => x !== n.id) : [...needs, n.id])} className={`w-full flex items-center gap-4 rounded-2xl p-4 border transition-colors min-h-[60px] ${on ? "bg-[#F97316] border-[#F97316]" : "bg-white/5 border-white/10"}`}>
                <n.icon className="w-6 h-6" />
                <span className="text-lg font-semibold flex-1 text-left">{n.id}</span>
                {on && <Check className="w-5 h-5" />}
              </button>
            );
          })}
        </div>
        <button data-testid="sos-needs-next" onClick={() => { captureGps(); setStep(4); }} className="w-full bg-white text-[#111827] rounded-2xl py-4 font-bold text-lg mt-6 min-h-[56px]">Continue</button>
      </div>
    );

  return shell(
    <div className="flex-1 flex flex-col">
      <h1 className="text-3xl font-bold font-head">Confirm your location</h1>
      <p className="text-white/60 mt-1 mb-5">Guardian AI captures your live location only for this emergency. Your home community is never overwritten.</p>
      <div className="rounded-3xl overflow-hidden border border-white/10 bg-[#1f2937] p-5">
        {locStatus === "locating" && <div className="flex items-center gap-3 text-white/70"><Loader2 className="w-5 h-5 animate-spin" /> Locating you…</div>}
        {locStatus === "ok" && <div className="flex items-start gap-3"><MapPin className="w-6 h-6 text-[#16A34A]" /><div><p className="font-semibold">Live location captured</p><p className="text-sm text-white/60">{coords.lat.toFixed(4)}, {coords.lng.toFixed(4)} · ±{Math.round(coords.accuracy)}m</p></div></div>}
        {locStatus === "fallback" && <div className="flex items-start gap-3"><MapPin className="w-6 h-6 text-[#F97316]" /><div><p className="font-semibold">Using community location</p><p className="text-sm text-white/60">Precise GPS unavailable — Guardian AI will use your home community area.</p></div></div>}
        {locStatus === "idle" && <button onClick={captureGps} data-testid="sos-locate" className="text-[#C4B5FD] font-semibold">Enable location</button>}
      </div>
      {locStatus === "ok" && coords && (
        <div className="mt-3">
          <LiveMap center={[coords.lat, coords.lng]} zoom={16} height={170} accuracyM={coords.accuracy}
            markers={[{ id: "you", type: "victim", lat: coords.lat, lng: coords.lng, pulse: true, label: "Your location" }]} testId="sos-location-map" />
        </div>
      )}
      <div className="mt-3 bg-white/5 rounded-2xl p-4 text-sm text-white/70">Emergency time and GPS accuracy are stored with this event, separate from your home community.</div>
      <button data-testid="sos-confirm" onClick={() => create()} disabled={busy || locStatus === "locating"} className="w-full bg-[#DC2626] rounded-3xl py-5 text-xl font-black mt-auto flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95 transition-transform">
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <>Alert Guardian AI</>}
      </button>
    </div>
  );
}
